import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AppBar from '../components/AppBar'
import SubmitButton from '../components/SubmitButton'
import { universityApi } from '../api/university'
import { useNoKeyboardResize } from '../hooks/useNoKeyboardResize'
import { toastUtil } from '../utils/toast'

const ERROR_COLOR = 'rgba(180, 5, 8, 0.8)' // #B40508 80%
const RESEND_COOLDOWN_SEC = 60
/** 서버의 `UniversityController::CODE_TTL_MIN` 과 같아야 한다. */
const CODE_TTL_SEC = 5 * 60

/**
 * 학교 메일로 받은 인증번호 입력.
 *
 * 문자 인증의 `PhoneVerify` 와 같은 모양이고, 다른 점은 둘이다.
 *   - 코드 유효 5분(문자는 3분). 메일은 문자보다 늦게 오는 일이 있다.
 *     **남은 시간 숫자는 띄우지 않는다** — 5:00 이 깜빡이면 급해 보이기만 하고,
 *     정작 알아야 할 때는 만료된 순간 하나다. 그때만 말해 준다.
 *
 * 6자리가 차면 바로 보낸다. 같은 숫자로 두 번 보내지는 않는다 —
 * 시도가 3회뿐이라 한 번 틀린 코드를 자동으로 또 소모하면 안 된다.
 *
 * 끝나면 완료 화면(`/university/done`)으로 `replace` 해서 간다. 뒤로가기로
 * 코드 입력 화면에 다시 떨어지면 이미 쓴 코드를 또 넣게 된다.
 */
const UniversityVerify: React.FC = () => {
  const navigate = useNavigate()
  useNoKeyboardResize()

  const [params] = useSearchParams()
  const universityId = Number(params.get('id') || 0)
  const email = params.get('email') || ''

  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [verifying, setVerifying] = useState(false)
  /*
   * 남은 시간은 '몇 초 남았나'를 1초씩 깎지 않고 **끝나는 시각**에서 잰다.
   * 학생은 번호를 보러 메일 앱으로 나갔다 온다 — 그동안 앱 화면의 타이머는 멈춰서,
   * 1초씩 깎던 때는 돌아와도 재전송이 그만큼 늦게 풀리고 만료도 늦게 알렸다.
   */
  const cooldownUntil = useRef(Date.now() + RESEND_COOLDOWN_SEC * 1000)
  const expiresAt = useRef(Date.now() + CODE_TTL_SEC * 1000)
  const left = (until: number) => Math.max(0, Math.ceil((until - Date.now()) / 1000))
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SEC)
  const [remain, setRemain] = useState(CODE_TTL_SEC)

  const expired = remain <= 0
  const isValid = code.length === 6 && !expired
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    const tick = () => {
      setCooldown(left(cooldownUntil.current))
      setRemain(left(expiresAt.current))
    }
    timerRef.current = setInterval(tick, 1000)
    // 메일 앱에서 돌아온 순간 바로 맞춘다(다음 1초를 기다리지 않게)
    document.addEventListener('visibilitychange', tick)
    window.addEventListener('focus', tick)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      document.removeEventListener('visibilitychange', tick)
      window.removeEventListener('focus', tick)
    }
  }, [])

  const mmss = (sec: number) =>
    `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`
  const cooldownText = mmss(cooldown)

  const handleVerify = useCallback(async () => {
    if (code.length !== 6 || expired || verifying) return
    setVerifying(true)
    setError('')
    try {
      const res = await universityApi.verifyCode(code)
      if (!res.verified) {
        setError(res.message || '인증번호를 다시 확인해주세요')
        return
      }
      // 완료 화면이 학교 이름·로고를 크게 보여준다 — 토스트는 겹쳐서 뺐다
      navigate('/university/done', { replace: true })
    } catch (e: any) {
      setError(e?.response?.data?.message || '인증번호를 다시 확인해주세요')
    } finally {
      setVerifying(false)
    }
  }, [code, expired, verifying, navigate])

  /**
   * 6자리가 차면 바로 보낸다. 버튼을 누르지 않아도 되게.
   *
   * `triedRef` 로 **같은 숫자는 한 번만** 보낸다. 틀린 코드가 그대로 남아 있는
   * 상태에서 자동 발송이 되풀이되면 시도 3회가 순식간에 날아간다.
   */
  const triedRef = useRef('')
  useEffect(() => {
    if (code.length !== 6 || expired || verifying) return
    if (triedRef.current === code) return
    triedRef.current = code
    handleVerify()
  }, [code, expired, verifying, handleVerify])

  const handleResend = async () => {
    if (cooldown > 0) return
    try {
      await universityApi.sendCode(universityId, email)
      toastUtil.info('인증번호를 다시 보냈어요')
      setCode('')
      setError('')
      triedRef.current = ''
      cooldownUntil.current = Date.now() + RESEND_COOLDOWN_SEC * 1000
      expiresAt.current = Date.now() + CODE_TTL_SEC * 1000
      setCooldown(RESEND_COOLDOWN_SEC)
      setRemain(CODE_TTL_SEC)
    } catch (e: any) {
      // 서버가 쿨다운이 남았다고 하면 화면의 대기 시간도 그에 맞춘다
      const wait = Number(e?.response?.data?.retryAfterSec)
      if (wait > 0) {
        cooldownUntil.current = Date.now() + wait * 1000
        setCooldown(wait)
      }
      toastUtil.error(e?.response?.data?.message || '재전송에 실패했습니다')
    }
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden pb-32px">
        <AppBar onBack={() => navigate(-1)} />

        <div className="flex-1 overflow-y-auto scrollbar-hide px-20px pt-40px">
          <p className="font-pretendard font-bold text-22px leading-135% text-brown-80%-main">
            인증번호를 입력해주세요
          </p>
          {email && (
            <p
              className="mt-8px font-pretendard font-regular text-15px leading-135%"
              style={{ color: 'rgba(75, 54, 33, 0.4)' }}
            >
              {email}로 보냈어요
            </p>
          )}

          <div className="mt-32px">
            <input
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                if (error) setError('')
              }}
              inputMode="numeric"
              type="tel"
              autoComplete="one-time-code"
              className="w-full bg-transparent border-none outline-none font-pretendard font-bold text-22px leading-135% py-8px"
              style={{ color: error ? ERROR_COLOR : 'rgba(75, 54, 33, 0.8)' }}
            />
            <div
              className="h-2px w-full"
              style={{
                backgroundColor: error || expired ? ERROR_COLOR : 'rgba(75, 54, 33, 0.2)',
              }}
            />

            {(error || expired) && (
              <div className="flex items-center gap-8px mt-12px">
                <img src="/images/signup-x.png" alt="" className="w-20px h-20px object-contain" />
                <span
                  className="font-pretendard font-bold text-15px"
                  style={{ color: ERROR_COLOR }}
                >
                  {error || '인증번호가 만료되었어요. 재전송해주세요'}
                </span>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleResend}
            disabled={cooldown > 0}
            className="mt-20px flex items-center gap-6px"
          >
            <span
              className={`font-pretendard font-bold text-14px underline ${
                cooldown > 0 ? 'text-472410-20%' : 'text-textColorText3'
              }`}
            >
              재전송
            </span>
            {cooldown > 0 && (
              <span className="font-pretendard font-regular text-14px text-textColorText3">
                {cooldownText}
              </span>
            )}
          </button>
        </div>

        <div className="w-full px-20px">
          <SubmitButton
            text="완료"
            disabled={!isValid}
            loading={verifying}
            onSubmit={handleVerify}
            onDisabled={() => {}}
          />
        </div>
      </div>
    </div>
  )
}

export default UniversityVerify
